import { useMemo } from "react";
import usePageAwareness from "./usePageAwareness";

export default function useLegacyPlatformPageAwareness({
  routeKey,
  pageTitle,
  platformKey,
  platformLabel,
  advertiserId,
  advertiserName,
  selectedLink,
  linkResolved,
  loading,
  showReport,
  loadError,
  pageCapabilities = [],
  pageToplineMetrics = [],
  timeframe = null,
  metricSignature = "",
  scopeKey = "",
  sourceState = "",
}) {
  const awareness = useMemo(() => {
    const reportReady = Boolean(selectedLink);
    const embedState = showReport ? (loading ? "Loading" : "Open") : "Closed";
    const reportStatus = !linkResolved
      ? "Resolving"
      : reportReady
      ? "Available"
      : "Unavailable";

    return {
      routeKey,
      pageTitle,
      pageType: "analytics",
      advertiserId: advertiserId ?? null,
      advertiserName: advertiserName || "",
      platformKey,
      platformLabel,
      toplineScope: pageToplineMetrics.length ? "page" : "aggregate",
      pageCapabilities,
      timeframe: timeframe || {
        kind: "live_embed",
        label: "Live report context",
      },
      pageToplineMetrics,
      aggregateToplineMetrics: [
        {
          id: "platform",
          label: "Platform",
          value: platformLabel,
          meta: "Current analytics surface",
        },
        advertiserName
          ? {
              id: "advertiser",
              label: "Advertiser",
              value: advertiserName,
              meta: advertiserId ? `ID ${advertiserId}` : "Current scope",
            }
          : null,
        {
          id: "report_status",
          label: "Report Status",
          value: reportStatus,
          meta: reportReady ? "Linked report found" : "Awaiting advertiser-linked report",
        },
        {
          id: "embed_state",
          label: "Embed State",
          value: embedState,
          meta: loadError ? loadError : loading ? "Report loading" : "Launcher aware",
        },
      ].filter(Boolean),
      metadata: {
        reportReady,
        linkResolved: Boolean(linkResolved),
        showReport: Boolean(showReport),
        hasLoadError: Boolean(loadError),
        metricSignature: String(metricSignature || ""),
        scopeKey: String(scopeKey || ""),
        sourceState: String(sourceState || ""),
      },
    };
  }, [
    advertiserId,
    advertiserName,
    linkResolved,
    loadError,
    loading,
    pageCapabilities,
    pageTitle,
    platformKey,
    platformLabel,
    routeKey,
    selectedLink,
    showReport,
    pageToplineMetrics,
    timeframe,
    metricSignature,
    scopeKey,
    sourceState,
  ]);

  usePageAwareness(awareness);
}
